/**
 * The demo lane's vocabulary and the parsing over it.
 *
 * Types live beside the only code that produces them rather than in a separate
 * file: there are four, and the section's own contract already has a `types.ts`
 * one level up.
 */

export type SeedingStatus = "provisioning" | "ready" | "failed";

export type SeedingPhase = "resolving_dataset" | "materializing" | "complete";

export interface SeedingProgress {
  readonly phase: SeedingPhase;
  readonly completedItems: number;
  readonly totalItems: number | null;
}

export interface SeedingProfile {
  readonly status: SeedingStatus | null;
  readonly progress: SeedingProgress | null;
}

export type SeedingAction = "retry" | "refresh";

interface ProgressWire {
  readonly phase?: unknown;
  readonly completed_items?: unknown;
  readonly total_items?: unknown;
}

export const POLL_INTERVAL_MS = 750;

/** Ceiling for the backed-off retry cadence. */
export const MAX_RETRY_DELAY_MS = 30_000;

/**
 * How long to wait before re-reading after `failures` consecutive failed reads.
 *
 * A seed that is running keeps the app blocked, so the poll has to continue
 * through an outage — but at the normal cadence a blocked tab would put ~80
 * requests a minute into a server that is already down, and every blocked tab
 * would do it, arriving together the moment it comes back.
 */
export function retryDelayMs(failures: number): number {
  return Math.min(POLL_INTERVAL_MS * 2 ** Math.max(0, failures - 1), MAX_RETRY_DELAY_MS);
}

export const EMPTY_PROFILE: SeedingProfile = {
  status: null,
  progress: null,
};

export function parseStatus(value: unknown): SeedingStatus | null {
  if (value === undefined || value === null) return null;
  if (value === "provisioning" || value === "ready" || value === "failed") {
    return value;
  }
  throw new Error("invalid dataset provisioning status");
}

function parsePhase(value: unknown): SeedingPhase {
  if (
    value === "resolving_dataset" ||
    value === "materializing" ||
    value === "complete"
  ) {
    return value;
  }
  throw new Error("invalid dataset provisioning phase");
}

export function parseProgress(value: unknown): SeedingProgress | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "object") {
    throw new Error("invalid dataset provisioning progress");
  }
  const wire = value as ProgressWire;
  if (
    typeof wire.completed_items !== "number" ||
    !Number.isSafeInteger(wire.completed_items) ||
    wire.completed_items < 0
  ) {
    throw new Error("invalid completed item count");
  }
  if (
    wire.total_items !== null &&
    (typeof wire.total_items !== "number" ||
      !Number.isSafeInteger(wire.total_items) ||
      wire.total_items < wire.completed_items)
  ) {
    throw new Error("invalid total item count");
  }
  return {
    phase: parsePhase(wire.phase),
    completedItems: wire.completed_items,
    totalItems: wire.total_items,
  };
}

